// src/pages/DocHistoryPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  listDocwriterHistory,
  getDocwriterHistoryStats,
  renameDocwriterHistory,
  archiveDocwriterHistory,
  restoreDocwriterHistory,
  deleteDocwriterHistory,
} from "../utils/docApiClient";

const PAGE_SIZE_OPTIONS = [8, 12, 20, 50];

const EMPTY_HISTORY_STATS = Object.freeze({
  total: null,
  draft: null,
  processing: null,
  finalized: null,
  error: null,
});

// Module-level cache survives normal SPA navigation and browser Back/Forward.
// A full browser refresh naturally clears it and requests fresh summary counts.
const HISTORY_STATS_CACHE_MS = 5 * 60 * 1000;
const historyStatsCache = new Map();
const historyStatsPromises = new Map();

const FILTERS = [
  { value: "all", label: "All" },
  { value: "ready", label: "Draft", dot: "#7C3AED" },
  { value: "processing", label: "Processing", dot: "#2563EB" },
  { value: "locked", label: "Finalized", dot: "#059669" },
  { value: "failed", label: "Error", dot: "#EF4444" },
];

const STATUS_META = {
  processing: {
    label: "Processing",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    dot: "#2563EB",
  },
  draft: {
    label: "Draft",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "#DDD6FE",
    dot: "#8B5CF6",
  },
  finalized: {
    label: "Finalized",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
    dot: "#16A34A",
  },
  error: {
    label: "Error",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    dot: "#EF4444",
  },
};

const PROCESSING_STATUSES = new Set([
  "processing",
  "queued",
  "reserved",
  "waiting_capacity",
  "dispatch_reserved",
  "dispatched",
  "worker_received",
  "verifying_sources",
  "extracting",
  "summarizing",
  "drafting",
  "saving",
  "generating",
  "revision_queued",
  "revision_processing",
  "finalizing",
]);

const ERROR_STATUSES = new Set([
  "error",
  "failed",
  "failure",
  "queue_failed",
  "billing_error",
  "canceled",
  "cancelled",
  "abandoned",
]);

function safeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
}

function cleanText(value, fallback = "—") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : fallback;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => cleanText(item, ""))
      .filter(Boolean)
      .join(", ");
    return joined || fallback;
  }
  if (typeof value === "object") {
    const preferred =
      value.title || value.label || value.name || value.summary || value.text;
    return preferred ? cleanText(preferred, fallback) : fallback;
  }
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (/^\{[\s\S]*\}$/.test(text) || /^\[[\s\S]*\]$/.test(text)) {
    return fallback;
  }
  return text;
}

function normalizeRevisionId(value) {
  const text = String(value || "").trim().toUpperCase();
  return /^(D1|R[1-9]\d*)$/.test(text) ? text : "";
}

function revisionNumber(value) {
  const normalized = normalizeRevisionId(value);
  if (!normalized) return -1;
  if (normalized === "D1") return 0;
  return Number(normalized.slice(1)) || -1;
}

function compareRevisionIds(a, b) {
  return revisionNumber(a) - revisionNumber(b);
}

function recordItems(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, item]) => ({
      revisionId: normalizeRevisionId(
        item?.revisionId || item?.revision_id || key,
      ),
      ...safeObject(item),
    }));
  }
  return [];
}

function collectRevisionIds(history) {
  const revisionState = safeObject(
    history?.revisionState || history?.revision_state,
  );
  const revisionIndex = safeObject(
    history?.revisionIndex || history?.revision_index,
  );
  const values = [
    ...recordItems(history?.revisions),
    ...recordItems(revisionIndex?.revisions),
  ];

  const ids = new Set();
  for (const item of values) {
    const id = normalizeRevisionId(
      item?.revisionId || item?.revision_id || item?.id,
    );
    if (id) ids.add(id);
  }

  const available = pickFirst(
    history?.availableRevisionIds,
    history?.available_revision_ids,
    revisionIndex?.availableRevisionIds,
    revisionIndex?.available_revision_ids,
    revisionState?.availableRevisionIds,
    revisionState?.available_revision_ids,
  );
  if (Array.isArray(available)) {
    available.forEach((value) => {
      const id = normalizeRevisionId(value);
      if (id) ids.add(id);
    });
  }

  const directIds = [
    history?.currentRevisionId,
    history?.current_revision_id,
    history?.finalizedRevisionId,
    history?.finalized_revision_id,
    revisionState?.currentRevisionId,
    revisionState?.current_revision_id,
    revisionState?.latestRevisionId,
    revisionState?.latest_revision_id,
    revisionState?.finalizedRevisionId,
    revisionState?.finalized_revision_id,
  ];
  directIds.forEach((value) => {
    const id = normalizeRevisionId(value);
    if (id) ids.add(id);
  });

  return Array.from(ids).sort(compareRevisionIds);
}

function rawStatus(history) {
  return String(
    pickFirst(
      history?.status,
      history?.rawStatus,
      history?.raw_status,
      history?.documentState,
      history?.document_state,
      "processing",
    ),
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function lifecycleStatus(history) {
  const status = rawStatus(history);
  const locked = Boolean(
    history?.locked ||
      history?.finalized ||
      status === "locked" ||
      status === "finalized",
  );
  const finalizedRevisionId = normalizeRevisionId(
    pickFirst(
      history?.finalizedRevisionId,
      history?.finalized_revision_id,
      history?.finalization?.selectedRevisionId,
      history?.finalization?.selected_revision_id,
    ),
  );

  if (
    history?.sourceCorrectionRequired ||
    history?.source_correction_required ||
    history?.needsSourceCorrection ||
    status === "needs_source_correction" ||
    status === "source_correction"
  ) {
    return "error";
  }
  if (ERROR_STATUSES.has(status) || history?.failed || history?.error) {
    return "error";
  }
  if (locked || finalizedRevisionId) return "finalized";
  if (
    PROCESSING_STATUSES.has(status) ||
    history?.processing ||
    safeObject(history?.operationState).activeOperationId ||
    safeObject(history?.operation_state).active_operation_id
  ) {
    return "processing";
  }
  return "draft";
}

function currentRevisionId(history, revisionIds) {
  const revisionState = safeObject(
    history?.revisionState || history?.revision_state,
  );
  const id = normalizeRevisionId(
    pickFirst(
      history?.currentRevisionId,
      history?.current_revision_id,
      revisionState?.currentRevisionId,
      revisionState?.current_revision_id,
      revisionState?.latestRevisionId,
      revisionState?.latest_revision_id,
    ),
  );
  if (id) return id;
  return revisionIds[revisionIds.length - 1] || "";
}

function finalizedRevisionId(history, revisionIds) {
  const revisionState = safeObject(
    history?.revisionState || history?.revision_state,
  );
  const finalization = safeObject(history?.finalization);
  return (
    normalizeRevisionId(
      pickFirst(
        history?.finalizedRevisionId,
        history?.finalized_revision_id,
        revisionState?.finalizedRevisionId,
        revisionState?.finalized_revision_id,
        finalization?.selectedRevisionId,
        finalization?.selected_revision_id,
      ),
    ) || revisionIds[revisionIds.length - 1] || ""
  );
}

function targetRevisionId(history) {
  const activeOperation = safeObject(
    history?.activeOperation || history?.active_operation,
  );
  const operationState = safeObject(
    history?.operationState || history?.operation_state,
  );
  return normalizeRevisionId(
    pickFirst(
      activeOperation?.targetRevisionId,
      activeOperation?.target_revision_id,
      operationState?.activeTargetRevisionId,
      operationState?.active_target_revision_id,
      history?.targetRevisionId,
      history?.target_revision_id,
    ),
  );
}

function normalizeHistoryItem(raw = {}) {
  const history = raw?.history && typeof raw.history === "object" ? raw.history : raw;
  const revisionIds = collectRevisionIds(history);
  const sessionId = String(
    pickFirst(history?.sessionId, history?.session_id, raw?.sessionId, raw?.session_id),
  ).trim();
  const jobId = String(
    pickFirst(history?.jobId, history?.job_id, raw?.jobId, raw?.job_id),
  ).trim();
  const lifecycle = lifecycleStatus(history);
  const currentRevision = currentRevisionId(history, revisionIds);
  const finalizedRevision = finalizedRevisionId(history, revisionIds);
  const targetRevision = targetRevisionId(history);
  const revisionCount = Math.max(
    0,
    Number(
      pickFirst(
        history?.revisionCount,
        history?.revision_count,
        history?.revisionState?.versionCount,
        history?.revision_state?.version_count,
        revisionIds.length,
      ),
    ) || revisionIds.length,
  );
  const title = String(
    pickFirst(
      history?.title,
      history?.displayTitle,
      history?.display_title,
      history?.subject,
      history?.subjectName,
      history?.subject_name,
      "Untitled Doc",
    ),
  ).trim();

  return {
    ...history,
    sessionId,
    jobId,
    title: title || "Untitled Doc",
    displayTitle: title || "Untitled Doc",
    lifecycle,
    rawStatus: rawStatus(history),
    revisionIds,
    revisionCount,
    currentRevisionId: currentRevision,
    finalizedRevisionId: finalizedRevision,
    targetRevisionId: targetRevision,
    sourceCount:
      Number(
        pickFirst(
          history?.sourceCount,
          history?.source_count,
          Array.isArray(history?.sources) ? history.sources.length : "",
        ),
      ) || 0,
    invalidSourceCount:
      Number(
        pickFirst(
          history?.invalidSourceCount,
          history?.invalid_source_count,
          Array.isArray(history?.invalidSources)
            ? history.invalidSources.length
            : "",
        ),
      ) || 0,
    createdAt: pickFirst(history?.createdAt, history?.created_at),
    updatedAt: pickFirst(
      history?.updatedAt,
      history?.updated_at,
      history?.completedAt,
      history?.completed_at,
      history?.createdAt,
      history?.created_at,
    ),
    archived: Boolean(history?.archived || raw?.archived),
    deleted: Boolean(history?.deleted || raw?.deleted),
    publishedId: String(
      pickFirst(
        history?.publishedId,
        history?.published_id,
        history?.finalizationId,
        history?.finalization_id,
        history?.finalization?.publishedId,
        history?.finalization?.published_id,
      ),
    ).trim(),
    errorMessage: String(
      pickFirst(
        history?.errorMessage,
        history?.error_message,
        history?.lastError?.message,
        history?.last_error?.message,
        history?.error?.message,
        "",
      ),
    ).trim(),
  };
}

function normalizeHistoryResponse(data) {
  const rows =
    data?.history ||
    data?.items ||
    data?.results ||
    data?.docwriterHistory ||
    data?.docwriter_history ||
    data?.sessions ||
    [];
  return Array.isArray(rows)
    ? rows.map(normalizeHistoryItem).filter((item) => item.sessionId && item.jobId)
    : [];
}

function pickOptionalCount(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.trunc(numeric));
    }
  }
  return null;
}

function normalizeHistoryStatsResponse(data = {}) {
  const source = safeObject(
    data?.stats || data?.summaryCounts || data?.summary_counts,
  );

  const needsCorrection = pickOptionalCount(
    source.needsSourceCorrection,
    source.needs_source_correction,
    data?.needsSourceCorrectionCount,
    data?.needs_source_correction_count,
  );
  const failed = pickOptionalCount(
    source.failed,
    data?.failedCount,
    data?.failed_count,
    source.error,
    data?.errorCount,
    data?.error_count,
  );

  return {
    total: pickOptionalCount(
      source.total,
      data?.totalDocuments,
      data?.total_documents,
      data?.totalCount,
      data?.total_count,
    ),
    draft: pickOptionalCount(
      source.ready,
      data?.readyCount,
      data?.ready_count,
    ),
    processing: pickOptionalCount(
      source.processing,
      data?.processingCount,
      data?.processing_count,
    ),
    finalized: pickOptionalCount(
      source.locked,
      data?.lockedCount,
      data?.locked_count,
    ),
    error:
      needsCorrection !== null || failed !== null
        ? Number(needsCorrection || 0) + Number(failed || 0)
        : null,
  };
}

function historyStatsCacheKey(userKey, showAll) {
  return `${String(userKey || "anonymous")}:${showAll ? "all" : "active"}`;
}

function unwrapDocwriterApiResponse(result, fallbackMessage) {
  if (result?.ok) return safeObject(result.data);

  const error = safeObject(result?.error);
  throw new Error(
    String(
      error.message ||
        result?.message ||
        fallbackMessage ||
        "The Doc Writer request could not be completed.",
    ).trim(),
  );
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?.seconds === "number"
          ? new Date(value.seconds * 1000)
          : typeof value?._seconds === "number"
            ? new Date(value._seconds * 1000)
            : new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date
      .toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      .replace(/,/g, "");
  } catch (_) {
    return "—";
  }
}

function formatTableDate(value) {
  if (!value) return { monthDay: "—", year: "" };

  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?.seconds === "number"
          ? new Date(value.seconds * 1000)
          : typeof value?._seconds === "number"
            ? new Date(value._seconds * 1000)
            : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return { monthDay: "—", year: "" };
    }

    return {
      monthDay: date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
      year: String(date.getFullYear()),
    };
  } catch (_) {
    return { monthDay: "—", year: "" };
  }
}

function percentText(value, total) {
  if (
    value === null ||
    value === undefined ||
    total === null ||
    total === undefined
  ) {
    return "Loading…";
  }
  if (!total) return "0%";
  return `${Math.round((Number(value || 0) / Number(total || 1)) * 100)}%`;
}

function statusMeta(itemOrStatus) {
  const lifecycle =
    typeof itemOrStatus === "string"
      ? itemOrStatus
      : itemOrStatus?.lifecycle || lifecycleStatus(itemOrStatus || {});
  return STATUS_META[lifecycle] || STATUS_META.processing;
}

function primaryAction(item) {
  if (item.lifecycle === "finalized") {
    return {
      label: "View",
      title: "Open finalized Doc in the Viewer",
      tone: "view",
    };
  }
  if (item.lifecycle === "processing") {
    return {
      label: "Progress",
      title: "Open live Doc processing status",
      tone: "progress",
    };
  }
  if (item.lifecycle === "draft") {
    return {
      label: "Edit",
      title: "Open the latest editable revision",
      tone: "edit",
    };
  }
  return {
    label: "Status",
    title: "Open this Doc's status page",
    tone: "error",
  };
}

function useIsDocHistoryCompact(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false;
  const [isCompact, setIsCompact] = useState(getInitial());
  useEffect(() => {
    const onResize = () => setIsCompact(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isCompact;
}

export default function DocHistoryPage() {
  const navigate = useNavigate();
  const isCompact = useIsDocHistoryCompact();

  const [idToken, setIdToken] = useState("");
  const [authUserKey, setAuthUserKey] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyStats, setHistoryStats] = useState(EMPTY_HISTORY_STATS);
  const [statsLoading, setStatsLoading] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("newest");
  const [pageSize, setPageSize] = useState(8);
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [menuOpenSessionId, setMenuOpenSessionId] = useState("");
  const [menuPos, setMenuPos] = useState(null);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("list");

  const refreshInFlightRef = useRef(false);
  const historyLoadedRef = useRef(false);
  const historyRequestSeqRef = useRef(0);
  const statsRequestSeqRef = useRef(0);
  const actionSubmittingRef = useRef(false);
  const menuAnchorRef = useRef(null);

  useEffect(() => {
    setViewMode("list");
    setMenuOpenSessionId("");
    setMenuPos(null);
    menuAnchorRef.current = null;
  }, [isCompact]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setDebouncedQuery(String(query || "").trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIdToken("");
        setAuthUserKey("");
        setAuthReady(true);
        setLoading(false);
        setHistoryItems([]);
        setHistoryStats(EMPTY_HISTORY_STATS);
        historyLoadedRef.current = false;
        return;
      }

      try {
        const token = await user.getIdToken();
        setAuthUserKey(user.uid || user.email || "");
        setIdToken(token || "");
      } catch (err) {
        setError(err?.message || "Unable to verify your login session.");
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadHistoryStats = useCallback(
    async ({
      token = idToken,
      force = false,
      archived = showAll,
    } = {}) => {
      if (!authReady || !token || !authUserKey) return false;

      const cacheKey = historyStatsCacheKey(authUserKey, archived);
      const requestId = ++statsRequestSeqRef.current;
      const cachedEntry = historyStatsCache.get(cacheKey);
      const cacheIsFresh =
        cachedEntry &&
        Date.now() - Number(cachedEntry.fetchedAt || 0) <
          HISTORY_STATS_CACHE_MS;

      if (!force && cacheIsFresh) {
        setHistoryStats(cachedEntry.data);
        setStatsLoading(false);
        return true;
      }

      setStatsLoading(true);

      let statsPromise =
        !force && historyStatsPromises.has(cacheKey)
          ? historyStatsPromises.get(cacheKey)
          : null;

      if (!statsPromise) {
        statsPromise = getDocwriterHistoryStats({
          idToken: token,
          showAll: archived,
        });
        historyStatsPromises.set(cacheKey, statsPromise);
      }

      try {
        const response = await statsPromise;
        const data = unwrapDocwriterApiResponse(
          response,
          "Unable to load Doc Writer history totals.",
        );
        const normalized = normalizeHistoryStatsResponse(data);

        historyStatsCache.set(cacheKey, {
          data: normalized,
          fetchedAt: Date.now(),
        });

        if (requestId === statsRequestSeqRef.current) {
          setHistoryStats(normalized);
        }
        return true;
      } catch (_) {
        // Summary counts are secondary. A stats failure must never block the
        // history rows, pagination, preview, or saved document access.
        if (requestId === statsRequestSeqRef.current) {
          setHistoryStats((current) =>
            current?.total === null ? EMPTY_HISTORY_STATS : current,
          );
        }
        return false;
      } finally {
        if (historyStatsPromises.get(cacheKey) === statsPromise) {
          historyStatsPromises.delete(cacheKey);
        }
        if (requestId === statsRequestSeqRef.current) {
          setStatsLoading(false);
        }
      }
    },
    [authReady, authUserKey, idToken, showAll],
  );

  const refreshHistory = useCallback(
    async ({
      initial = false,
      forceTokenRefresh = false,
      silent = false,
      targetPage = page,
    } = {}) => {
      if (!authReady || !idToken) return false;

      const requestId = ++historyRequestSeqRef.current;
      refreshInFlightRef.current = true;

      if (initial) setLoading(true);
      else setPageLoading(true);
      if (!silent) setRefreshing(true);
      setError("");

      try {
        let token = idToken;
        if (forceTokenRefresh) {
          const currentUser = getAuth().currentUser;
          if (!currentUser) {
            throw new Error(
              "Your login session is no longer available. Please sign in again.",
            );
          }
          token = await currentUser.getIdToken(true);
          if (token && token !== idToken) setIdToken(token);
        }

        const response = await listDocwriterHistory({
          idToken: token,
          page: targetPage,
          pageSize,
          query: debouncedQuery,
          status: statusFilter,
          sort: sortKey,
          showAll,
          lookahead: true,
          includeStats: false,
        });
        const data = unwrapDocwriterApiResponse(
          response,
          "Unable to load Doc Writer history.",
        );

        if (requestId !== historyRequestSeqRef.current) return false;

        const rows = normalizeHistoryResponse(data);
        const pagination = safeObject(data?.pagination);
        const responsePage = Math.max(
          1,
          Number(data?.page || pagination?.page || targetPage || 1),
        );

        setHistoryItems(rows);
        setPage(responsePage);
        setHasPrevious(
          Boolean(
            data?.hasPrevious ??
              data?.has_previous ??
              pagination?.hasPrevious ??
              pagination?.has_previous ??
              responsePage > 1,
          ),
        );
        setHasNext(
          Boolean(
            data?.hasNext ??
              data?.has_next ??
              pagination?.hasNext ??
              pagination?.has_next ??
              false,
          ),
        );
        setSelectedSessionId((current) => {
          if (current && rows.some((row) => row.sessionId === current)) {
            return current;
          }
          return rows[0]?.sessionId || "";
        });

        historyLoadedRef.current = true;
        return true;
      } catch (err) {
        if (requestId !== historyRequestSeqRef.current) return false;
        setError(err?.message || "Unable to load Doc History.");
        if (initial) setHistoryItems([]);
        return false;
      } finally {
        if (requestId === historyRequestSeqRef.current) {
          refreshInFlightRef.current = false;
          if (initial) setLoading(false);
          setPageLoading(false);
          if (!silent) setRefreshing(false);
        }
      }
    },
    [
      authReady,
      idToken,
      page,
      pageSize,
      debouncedQuery,
      statusFilter,
      sortKey,
      showAll,
    ],
  );

  // On the first authenticated render these independent effects start the
  // lightweight 8+1 history request and complete-summary request in parallel.
  useEffect(() => {
    if (!authReady || !idToken) return;
    refreshHistory({
      initial: !historyLoadedRef.current,
      silent: true,
      targetPage: page,
    });
  }, [authReady, idToken, page, refreshHistory]);

  useEffect(() => {
    if (!authReady || !idToken || !authUserKey) return;
    loadHistoryStats({ force: false, archived: showAll });
  }, [authReady, idToken, authUserKey, showAll, loadHistoryStats]);

  const safePage = Math.max(1, page);
  const pageItems = historyItems;
  const visibleStart = pageItems.length
    ? (safePage - 1) * pageSize + 1
    : 0;
  const visibleEnd = pageItems.length
    ? visibleStart + pageItems.length - 1
    : 0;
  const isUnfilteredPage =
    !String(debouncedQuery || "").trim() && statusFilter === "all";

  const totalCount = historyStats.total;
  const draftCount = historyStats.draft;
  const processingCount = historyStats.processing;
  const finalizedCount = historyStats.finalized;
  const selectedItem = useMemo(
    () =>
      historyItems.find((item) => item.sessionId === selectedSessionId) ||
      historyItems[0] ||
      null,
    [historyItems, selectedSessionId],
  );

  const openEditor = useCallback(
    (item) => {
      if (!item?.jobId || !item?.sessionId) return;
      try {
        sessionStorage.setItem(`docw:launch:${item.sessionId}`, "1");
      } catch (_) {
        // The Editor can still use the explicit History navigation state.
      }
      const params = new URLSearchParams({
        jobId: item.jobId,
        sessionId: item.sessionId,
      });
      navigate(`/docwriter/editor?${params.toString()}`, {
        state: {
          fromDocHistory: true,
          jobId: item.jobId,
          sessionId: item.sessionId,
          revisionId: item.currentRevisionId || "",
        },
      });
    },
    [navigate],
  );

  const openViewer = useCallback(
    (item) => {
      if (!item?.jobId || !item?.sessionId) return;
      const params = new URLSearchParams({
        jobId: item.jobId,
        sessionId: item.sessionId,
        revisionId:
          item.finalizedRevisionId || item.currentRevisionId || "D1",
      });
      if (item.publishedId) params.set("publishedId", item.publishedId);
      navigate(`/docwriter/viewer?${params.toString()}`, {
        state: {
          fromDocHistory: true,
          jobId: item.jobId,
          sessionId: item.sessionId,
          revisionId:
            item.finalizedRevisionId || item.currentRevisionId || "D1",
          finalized: true,
          locked: true,
        },
      });
    },
    [navigate],
  );

  const openHistoryItem = useCallback(
    (item) => {
      if (!item) return;
      if (item.lifecycle === "finalized") {
        openViewer(item);
        return;
      }
      openEditor(item);
    },
    [openEditor, openViewer],
  );

  const closeHistoryMenu = useCallback(() => {
    setMenuOpenSessionId("");
    setMenuPos(null);
    menuAnchorRef.current = null;
  }, []);

  const openHistoryMenu = useCallback((anchor, item) => {
    if (!anchor || !item?.sessionId) return;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth || 1024;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 768;
    const menuWidth = 176;
    const menuHeight = 132;
    const edge = 10;
    let top = rect.bottom + 8;
    let left = rect.right - menuWidth;
    if (viewportHeight - rect.bottom < menuHeight + edge) {
      top = Math.max(edge, rect.top - menuHeight - 8);
    }
    left = Math.max(edge, Math.min(left, viewportWidth - menuWidth - edge));
    menuAnchorRef.current = anchor;
    setMenuOpenSessionId(item.sessionId);
    setMenuPos({ top, left });
  }, []);

  const toggleHistoryMenu = useCallback(
    (anchor, item) => {
      if (menuOpenSessionId === item?.sessionId) {
        closeHistoryMenu();
        return;
      }
      openHistoryMenu(anchor, item);
    },
    [closeHistoryMenu, menuOpenSessionId, openHistoryMenu],
  );

  useEffect(() => {
    if (!menuOpenSessionId) return undefined;
    const onPointerDown = (event) => {
      if (event.target?.closest?.("#doc-history-fixed-menu")) return;
      if (menuAnchorRef.current?.contains?.(event.target)) return;
      closeHistoryMenu();
    };
    const onViewportChange = () => closeHistoryMenu();
    document.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [menuOpenSessionId, closeHistoryMenu]);

  const handleManualRefresh = async () => {
    if (refreshInFlightRef.current) return;
    setRefreshing(true);

    await new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
      } else {
        resolve();
      }
    });

    try {
      await refreshHistory({
        forceTokenRefresh: true,
        silent: true,
        targetPage: page,
      });
      await loadHistoryStats({ force: true, archived: showAll });
    } finally {
      setRefreshing(false);
    }
  };

  const closeActionModal = useCallback(
    (force = false) => {
      if (actionSubmittingRef.current && !force) return;
      setActionModal(null);
      setRenameValue("");
    },
    [],
  );

  const handleRename = (item) => {
    closeHistoryMenu();
    setRenameValue(item?.displayTitle || item?.title || "");
    setActionModal({ type: "rename", item });
  };

  const handleArchiveToggle = (item) => {
    closeHistoryMenu();
    setActionModal({ type: item?.archived ? "restore" : "archive", item });
  };

  const handleDelete = (item) => {
    closeHistoryMenu();
    setActionModal({ type: "delete", item });
  };

  const confirmActionModal = async () => {
    const item = actionModal?.item;
    const actionType = actionModal?.type;
    if (!item?.sessionId || !actionType || actionSubmittingRef.current) return;

    const cleanName = String(renameValue || "").trim();
    if (actionType === "rename" && !cleanName) {
      setError("Please enter a name for this Doc.");
      return;
    }

    actionSubmittingRef.current = true;
    setActionSubmitting(true);
    setError("");
    await new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
      } else {
        resolve();
      }
    });

    try {
      let response = null;

      if (actionType === "rename") {
        response = await renameDocwriterHistory(
          item.sessionId,
          cleanName,
          { idToken },
        );
      } else if (actionType === "archive") {
        response = await archiveDocwriterHistory(
          item.sessionId,
          { idToken },
        );
      } else if (actionType === "restore") {
        response = await restoreDocwriterHistory(
          item.sessionId,
          { idToken },
        );
      } else if (actionType === "delete") {
        response = await deleteDocwriterHistory(
          item.sessionId,
          { idToken },
        );
      }

      unwrapDocwriterApiResponse(
        response,
        "Unable to update this Doc.",
      );

      actionSubmittingRef.current = false;
      setActionSubmitting(false);
      closeActionModal(true);

      await refreshHistory({
        silent: true,
        targetPage: page,
      });
      if (actionType !== "rename") {
        await loadHistoryStats({ force: true, archived: showAll });
      }
    } catch (err) {
      actionSubmittingRef.current = false;
      setActionSubmitting(false);
      setError(err?.message || "Unable to update this Doc.");
    }
  };

  if (!authReady) {
    return (
      <div className="doc-history-page" style={styles.outerWrap}>
        <div style={styles.loadingCard}>
          <Spinner size={24} />
          <div style={styles.loadingTitle}>Preparing Doc History…</div>
        </div>
      </div>
    );
  }

  if (!idToken) {
    return (
      <div className="doc-history-page" style={styles.outerWrap}>
        <section style={styles.emptyState}>
          <div style={styles.emptyIcon}>◇</div>
          <h1 style={styles.emptyTitle}>Sign in to view Doc History</h1>
          <p style={styles.emptyText}>
            Your processing Docs, drafts, revisions, and finalized Docs are saved
            under your account.
          </p>
          <Link to="/login" style={styles.primaryButton}>
            Sign in
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="doc-history-page" style={styles.outerWrap}>
      <style>{`
        @keyframes docHistorySpin { to { transform: rotate(360deg); } }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }

        .doc-history-page,
        .doc-history-page *,
        .doc-history-layout,
        .doc-history-layout > *,
        .doc-history-row,
        .doc-history-card,
        .doc-history-actions,
        .doc-history-preview {
          box-sizing: border-box;
          min-width: 0;
          max-width: 100%;
        }

        .doc-history-page {
          width: 100%;
          overflow-x: hidden;
          contain: inline-size;
        }

        .doc-history-page-buttons {
          max-width: 100%;
          overflow-x: auto;
          padding-bottom: 2px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .doc-history-desktop-filter-row {
          display: flex;
        }

        .doc-history-mobile-utility-row,
        .doc-history-results-toolbar {
          display: none;
        }

        @media (max-width: 1024px) {
          .doc-history-page {
            width: 100% !important;
            max-width: 100% !important;
            padding: 14px 12px 34px !important;
            overflow-x: hidden !important;
          }

          .doc-history-hero {
            display: block !important;
            text-align: center !important;
          }

          .doc-history-hero > div {
            margin-left: auto !important;
            margin-right: auto !important;
          }

          .doc-history-hero .doc-history-new-button {
            margin: 18px auto 0 !important;
            max-width: 100% !important;
            justify-content: center !important;
          }

          .doc-history-title {
            font-size: 28px !important;
            line-height: 1.18 !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .doc-history-subtitle {
            margin-left: auto !important;
            margin-right: auto !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .doc-history-layout {
            grid-template-columns: minmax(0, 1fr) !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .doc-history-preview {
            position: static !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow: hidden !important;
          }

          .doc-history-controls {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
            gap: 10px !important;
          }

          .doc-history-search {
            grid-column: 1 / -1 !important;
          }

          .doc-history-desktop-filter-row {
            display: none !important;
          }

          .doc-history-mobile-utility-row {
            display: flex !important;
          }

          .doc-history-stat-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 12px !important;
          }

          .doc-history-stat-card {
            min-height: 112px !important;
            padding: 15px !important;
            gap: 12px !important;
            align-items: flex-start !important;
          }

          .doc-history-stat-icon {
            width: 44px !important;
            height: 44px !important;
            border-radius: 14px !important;
            font-size: 18px !important;
            flex: 0 0 auto !important;
          }

          .doc-history-results-toolbar {
            display: flex !important;
          }

          .doc-history-desktop-header {
            display: none !important;
          }

          .doc-history-card-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .doc-history-card,
          .doc-history-card * {
            min-width: 0 !important;
            max-width: 100% !important;
          }

          .doc-history-card-title,
          .doc-history-row-title {
            min-width: 0 !important;
            max-width: 100% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            overflow-wrap: normal !important;
            word-break: normal !important;
          }

          .doc-history-actions {
            width: 100% !important;
            max-width: 100% !important;
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) auto !important;
            align-items: center !important;
            gap: 9px !important;
          }

          .doc-history-actions > button:first-child {
            width: 100% !important;
            min-width: 0 !important;
            white-space: normal !important;
          }

          .doc-history-pagination {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 15px 12px !important;
          }

          .doc-history-page-buttons {
            width: 100% !important;
            justify-content: flex-start !important;
          }
        }
      `}</style>

      <header className="doc-history-hero" style={styles.hero}>
        <div>
          <div style={styles.kicker}>Doc Writer</div>
          <h1 className="doc-history-title" style={styles.title}>
            Doc History
          </h1>
          <p className="doc-history-subtitle" style={styles.subtitle}>
            Review drafts, track revisions, check processing status, and open
            finalized Docs.
          </p>
        </div>

        <Link
          to="/docwriter/start"
          className="doc-history-new-button"
          style={styles.newButton}
        >
          <span style={styles.newButtonIcon}>＋</span>
          New Doc
        </Link>
      </header>

      {error ? (
        <div style={styles.errorBox}>
          <strong>Notice:</strong> {error}
        </div>
      ) : null}

      <div
        className="doc-history-layout"
        style={{
          ...styles.layout,
          ...(!isCompact && previewCollapsed ? styles.layoutPreviewCollapsed : null),
        }}
      >
        <main style={styles.mainColumn}>
          <section style={styles.toolbarPanel}>
            <div className="doc-history-controls" style={styles.controlsGrid}>
              <label className="doc-history-search" style={styles.searchBox}>
                <span style={styles.searchIcon}>⌕</span>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search Doc name, revision, or status"
                  style={styles.searchInput}
                />
              </label>

              <select
                value={statusFilter}
                onChange={(event) => {
                  setPage(1);
                  setStatusFilter(event.target.value);
                }}
                style={styles.select}
              >
                <option value="all">Status: All</option>
                <option value="ready">Draft</option>
                <option value="processing">Processing</option>
                <option value="locked">Finalized</option>
                <option value="failed">Error</option>
              </select>

              <select
                value={sortKey}
                onChange={(event) => {
                  setPage(1);
                  setSortKey(event.target.value);
                }}
                style={styles.select}
              >
                <option value="newest">Sort: Last updated</option>
                <option value="oldest">Sort: Oldest first</option>
                <option value="title_asc">Sort: Name A–Z</option>
                <option value="title_desc">Sort: Name Z–A</option>
              </select>

              <select
                value={pageSize}
                onChange={(event) => {
                  setPage(1);
                  setPageSize(Number(event.target.value) || 8);
                }}
                style={styles.select}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="doc-history-desktop-filter-row"
              style={styles.filterRow}
            >
              {FILTERS.map((filter) => (
                <FilterButton
                  key={filter.value}
                  active={statusFilter === filter.value}
                  onClick={() => {
                    setPage(1);
                    setStatusFilter(filter.value);
                  }}
                  label={filter.label}
                  dot={filter.dot}
                />
              ))}

              <span style={styles.filterSpacer} />

              <RefreshButton refreshing={refreshing} onClick={handleManualRefresh} />
              <ShowArchivedToggle
                checked={showAll}
                onChange={(checked) => {
                  setPage(1);
                  setShowAll(checked);
                }}
              />
            </div>

            <div
              className="doc-history-mobile-utility-row"
              style={styles.mobileUtilityRow}
            >
              <ShowArchivedToggle
                checked={showAll}
                onChange={(checked) => {
                  setPage(1);
                  setShowAll(checked);
                }}
              />
              <RefreshButton refreshing={refreshing} onClick={handleManualRefresh} />
            </div>

            <div className="doc-history-stat-grid" style={styles.statGrid}>
              <StatCard
                icon="▣"
                label="Total Docs"
                value={totalCount}
                subtext={
                  statsLoading && totalCount === null
                    ? "Loading summary…"
                    : "All time"
                }
                color="#2563EB"
              />
              <StatCard
                icon="✎"
                label="Drafts"
                value={draftCount}
                subtext={percentText(draftCount, totalCount)}
                color="#7C3AED"
              />
              <StatCard
                icon="◔"
                label="Processing"
                value={processingCount}
                subtext={percentText(processingCount, totalCount)}
                color="#2563EB"
              />
              <StatCard
                icon="✓"
                label="Finalized"
                value={finalizedCount}
                subtext={percentText(finalizedCount, totalCount)}
                color="#059669"
              />
            </div>
          </section>

          <section style={styles.tablePanel}>
            <div className="doc-history-results-toolbar" style={styles.resultsToolbar}>
              <div>
                <div style={styles.resultsEyebrow}>History results</div>
                <div style={styles.resultsCount}>
                  {isUnfilteredPage && totalCount !== null
                    ? `${totalCount.toLocaleString()} Docs`
                    : `${pageItems.length.toLocaleString()} shown on this page`}
                </div>
              </div>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </div>

            {!isCompact ? (
              <div className="doc-history-desktop-header" style={styles.tableHeader}>
                <div style={styles.centerHeader}>Documents</div>
                <div style={styles.centerHeader}>Status</div>
                <div style={styles.centerHeader}>Revision</div>
                <div style={styles.centerHeader}>Updated</div>
                <div style={styles.actionHeader}>Actions</div>
              </div>
            ) : null}

            {loading ? (
              <div style={styles.loadingRows}>
                <Spinner size={22} />
                <span>Loading Doc History…</span>
              </div>
            ) : pageItems.length ? (
              isCompact && viewMode === "grid" ? (
                <div className="doc-history-card-grid" style={styles.mobileCardGrid}>
                  {pageItems.map((item) => (
                    <HistoryCard
                      key={item.sessionId}
                      item={item}
                      active={item.sessionId === selectedSessionId}
                      onSelect={() => setSelectedSessionId(item.sessionId)}
                      onOpen={() => openHistoryItem(item)}
                      onToggleMenu={(anchor) => toggleHistoryMenu(anchor, item)}
                    />
                  ))}
                </div>
              ) : (
                <div style={styles.rowsWrap}>
                  {pageItems.map((item) => (
                    <HistoryRow
                      key={item.sessionId}
                      item={item}
                      isCompact={isCompact}
                      active={item.sessionId === selectedSessionId}
                      onSelect={() => setSelectedSessionId(item.sessionId)}
                      onOpen={() => openHistoryItem(item)}
                      onToggleMenu={(anchor) => toggleHistoryMenu(anchor, item)}
                    />
                  ))}
                </div>
              )
            ) : (
              <EmptyHistoryState />
            )}

            <div className="doc-history-pagination" style={styles.pagination}>
              <div style={styles.paginationText}>
                {pageLoading ? (
                  <span style={styles.paginationLoading}>
                    <Spinner size={14} />
                    Loading page…
                  </span>
                ) : (
                  <>
                    Showing {visibleStart} to {visibleEnd}{" "}
                    {isUnfilteredPage && totalCount !== null
                      ? `of ${totalCount.toLocaleString()} Docs`
                      : "matching Docs"}
                  </>
                )}
              </div>

              <div
                className="doc-history-page-buttons"
                style={styles.pageButtons}
              >
                {hasPrevious && safePage > 1 ? (
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={pageLoading}
                    style={styles.pageButton}
                    aria-label="Previous Doc History page"
                  >
                    ‹
                  </button>
                ) : null}

                {Array.from({ length: safePage }, (_, index) => index + 1).map(
                  (pageNumber) => {
                    const isCurrentPage = pageNumber === safePage;
                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        disabled={pageLoading || isCurrentPage}
                        style={{
                          ...styles.pageButton,
                          ...(isCurrentPage ? styles.pageButtonActive : null),
                          ...(isCurrentPage ? styles.currentPageButton : null),
                        }}
                        aria-label={
                          isCurrentPage
                            ? `Current page ${pageNumber}`
                            : `Go to Doc History page ${pageNumber}`
                        }
                        aria-current={isCurrentPage ? "page" : undefined}
                      >
                        {pageNumber}
                      </button>
                    );
                  },
                )}

                {hasNext ? (
                  <button
                    type="button"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={pageLoading}
                    style={styles.pageButton}
                    aria-label="Next Doc History page"
                  >
                    ›
                  </button>
                ) : null}
              </div>
            </div>
          </section>
        </main>

        <aside
          className="doc-history-preview"
          style={
            previewCollapsed ? styles.previewCollapsedPanel : styles.previewPanel
          }
        >
          {previewCollapsed ? (
            <button
              type="button"
              onClick={() => setPreviewCollapsed(false)}
              style={styles.showPreviewButton}
              title="Show Doc preview"
            >
              Show Preview
            </button>
          ) : (
            <>
              <div style={styles.previewHeader}>
                <h2 style={styles.previewTitle}>Doc Preview</h2>
                <button
                  type="button"
                  onClick={() => setPreviewCollapsed(true)}
                  style={styles.collapsePreviewButton}
                  title="Collapse Doc preview"
                >
                  Collapse ›
                </button>
              </div>

              {selectedItem ? (
                <DocPreview item={selectedItem} onOpen={() => openHistoryItem(selectedItem)} />
              ) : (
                <div style={styles.previewEmpty}>
                  <div style={styles.emptyIcon}>▤</div>
                  <h3>No Doc selected</h3>
                  <p>Create a new Doc or select a saved Doc to preview its status.</p>
                  <Link to="/docwriter/start" style={styles.primaryButton}>
                    New Doc
                  </Link>
                </div>
              )}
            </>
          )}
        </aside>
      </div>

      <HistoryActionMenu
        item={
          historyItems.find((row) => row.sessionId === menuOpenSessionId) || null
        }
        position={menuPos}
        onRename={() => {
          const item = historyItems.find(
            (row) => row.sessionId === menuOpenSessionId,
          );
          if (item) handleRename(item);
        }}
        onArchiveToggle={() => {
          const item = historyItems.find(
            (row) => row.sessionId === menuOpenSessionId,
          );
          if (item) handleArchiveToggle(item);
        }}
        onDelete={() => {
          const item = historyItems.find(
            (row) => row.sessionId === menuOpenSessionId,
          );
          if (item) handleDelete(item);
        }}
      />

      <ActionModal
        actionModal={actionModal}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        submitting={actionSubmitting}
        onCancel={closeActionModal}
        onConfirm={confirmActionModal}
      />
    </div>
  );
}

function FilterButton({ active, label, dot, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.filterButton,
        ...(active ? styles.filterButtonActive : null),
      }}
    >
      {dot ? <span style={{ ...styles.filterDot, background: dot }} /> : null}
      {label}
    </button>
  );
}

function StatCard({ icon, label, value, subtext, color }) {
  const displayValue =
    value === null || value === undefined
      ? "—"
      : Number(value || 0).toLocaleString();

  return (
    <div className="doc-history-stat-card" style={styles.statCard}>
      <div
        className="doc-history-stat-icon"
        style={{ ...styles.statIcon, background: `${color}12`, color }}
      >
        {icon}
      </div>
      <div>
        <div style={styles.statLabel}>{label}</div>
        <div style={styles.statValue}>{displayValue}</div>
        <div style={styles.statSubtext}>{subtext}</div>
      </div>
    </div>
  );
}

function StatusPill({ item }) {
  const meta = statusMeta(item);
  return (
    <span
      style={{
        ...styles.statusPill,
        color: meta.color,
        background: meta.bg,
        borderColor: meta.border,
      }}
    >
      <span style={{ ...styles.statusDot, background: meta.dot }} />
      {meta.label}
    </span>
  );
}

function StatusCell({ item, compact = false }) {
  return (
    <div style={compact ? styles.mobileStatusCell : styles.centerCell}>
      <StatusPill item={item} />
    </div>
  );
}

function RevisionSummary({ item }) {
  const current =
    item.lifecycle === "finalized"
      ? item.finalizedRevisionId || item.currentRevisionId
      : item.currentRevisionId || item.targetRevisionId;

  return (
    <span
      style={{
        ...styles.scoreBadge,
        color: "#4338CA",
        background: "#EEF2FF",
        border: "1px solid #C7D2FE",
      }}
    >
      {current || "D1"}
    </span>
  );
}

function HistoryActions({ item, onOpen, onToggleMenu }) {
  const action = primaryAction(item);
  return (
    <div className="doc-history-actions" style={styles.rowActions}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        style={{
          ...styles.openButton,
          ...(action.tone === "progress" ? styles.progressButton : null),
          ...(action.tone === "error" ? styles.errorStatusButton : null),
        }}
        title={action.title}
      >
        {action.label}
      </button>
      <MoreActionsButton onToggleMenu={onToggleMenu} />
    </div>
  );
}

function HistoryRow({
  item,
  isCompact,
  active,
  onSelect,
  onOpen,
  onToggleMenu,
}) {
  const meta = statusMeta(item);
  const revision =
    item.lifecycle === "finalized"
      ? item.finalizedRevisionId || item.currentRevisionId || "D1"
      : item.currentRevisionId || item.targetRevisionId || "D1";
  const tableDate = formatTableDate(item.updatedAt || item.createdAt);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className="doc-history-row"
      style={{
        ...(isCompact ? styles.mobileRowButton : styles.rowButton),
        ...(active ? styles.rowButtonActive : null),
      }}
    >
      {isCompact ? (
        <>
          <div style={styles.mobileListTopRow}>
            <div
              style={{
                ...styles.fileIcon,
                background: `${meta.color}10`,
                color: meta.color,
              }}
            >
              ▧
            </div>

            <div style={styles.mobileListTitleWrap}>
              <div
                className="doc-history-row-title"
                style={styles.caseTitle}
                title={item.displayTitle}
              >
                <span style={styles.caseTitleText}>{item.displayTitle}</span>
                {item.archived ? (
                  <span style={styles.archivedInline}>Archived</span>
                ) : null}
              </div>
            </div>

            <div style={styles.mobileListStatus}>
              <StatusPill item={item} />
            </div>
          </div>

          <div style={styles.mobileMetaLine}>
            <span style={styles.mobileMetaPill}>Revision {revision}</span>
            <span style={styles.mobileMetaPill}>
              {formatDateTime(item.updatedAt || item.createdAt)}
            </span>
          </div>

          <div style={styles.mobileListActions}>
            <HistoryActions
              item={item}
              onOpen={onOpen}
              onToggleMenu={onToggleMenu}
            />
          </div>
        </>
      ) : (
        <>
          <div style={styles.caseCell}>
            <div
              style={{
                ...styles.fileIcon,
                background: `${meta.color}10`,
                color: meta.color,
              }}
            >
              ▧
            </div>

            <div style={styles.caseTextBlock}>
              <div
                className="doc-history-row-title"
                style={styles.caseTitle}
                title={item.displayTitle}
              >
                <span style={styles.caseTitleText}>{item.displayTitle}</span>
                {item.archived ? (
                  <span style={styles.archivedInline}>Archived</span>
                ) : null}
              </div>
            </div>
          </div>

          <StatusCell item={item} />

          <div style={styles.centerCell}>
            <RevisionSummary item={item} />
          </div>

          <div style={styles.centerCell}>
            <span style={styles.updatedText}>
              <span>{tableDate.monthDay}</span>
              {tableDate.year ? <span>{tableDate.year}</span> : null}
            </span>
          </div>

          <HistoryActions
            item={item}
            onOpen={onOpen}
            onToggleMenu={onToggleMenu}
          />
        </>
      )}
    </div>
  );
}

function HistoryCard({ item, active, onSelect, onOpen, onToggleMenu }) {
  const meta = statusMeta(item);
  const revision =
    item.lifecycle === "finalized"
      ? item.finalizedRevisionId || item.currentRevisionId || "D1"
      : item.currentRevisionId || item.targetRevisionId || "D1";

  return (
    <article
      className="doc-history-card"
      style={{
        ...styles.mobileCard,
        ...(active ? styles.mobileCardActive : null),
      }}
      onClick={onSelect}
    >
      <div style={styles.mobileCardHeader}>
        <div
          style={{
            ...styles.mobileCardIcon,
            background: `${meta.color}10`,
            color: meta.color,
          }}
        >
          ▧
        </div>

        <div style={styles.mobileCardTitleWrap}>
          <h3
            className="doc-history-card-title"
            style={styles.mobileCardTitle}
            title={item.displayTitle}
          >
            {item.displayTitle}
          </h3>
        </div>

        <div style={styles.mobileCardStatusGroup}>
          <StatusPill item={item} />
          {item.archived ? (
            <span style={styles.archivedCardBadge}>Archived</span>
          ) : null}
        </div>
      </div>

      <div style={styles.mobileCardMetrics}>
        <div style={styles.mobileMetric}>
          <span style={styles.mobileMetricLabel}>Revision</span>
          <strong style={styles.mobileMetricValue}>{revision}</strong>
        </div>

        <div style={styles.mobileMetric}>
          <span style={styles.mobileMetricLabel}>Updated</span>
          <strong style={styles.mobileMetricDate}>
            {formatDateTime(item.updatedAt || item.createdAt)}
          </strong>
        </div>
      </div>

      <HistoryActions
        item={item}
        onOpen={onOpen}
        onToggleMenu={onToggleMenu}
      />
    </article>
  );
}

function MoreActionsButton({ onToggleMenu }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggleMenu(event.currentTarget);
      }}
      style={styles.moreButton}
      aria-label="More actions"
      title="Rename, archive, or delete"
    >
      ⋮
    </button>
  );
}

function HistoryActionMenu({
  item,
  position,
  onRename,
  onArchiveToggle,
  onDelete,
}) {
  if (!item || !position) return null;
  return (
    <div
      id="doc-history-fixed-menu"
      style={{ ...styles.actionMenu, top: position.top, left: position.left }}
      role="menu"
    >
      <button type="button" style={styles.actionMenuItem} onClick={onRename}>
        Rename
      </button>
      <button type="button" style={styles.actionMenuItem} onClick={onArchiveToggle}>
        {item.archived ? "Restore" : "Archive"}
      </button>
      <button
        type="button"
        style={{ ...styles.actionMenuItem, ...styles.actionMenuItemDanger }}
        onClick={onDelete}
      >
        Delete
      </button>
    </div>
  );
}

function RefreshButton({ refreshing, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={refreshing}
      style={{
        ...styles.refreshButton,
        ...(refreshing ? styles.refreshButtonDisabled : null),
      }}
      title="Refresh Doc History"
      aria-label="Refresh Doc History"
    >
      {refreshing ? (
        <Spinner size={15} color="#2563EB" />
      ) : (
        <span style={styles.refreshIcon}>↻</span>
      )}
      <span>{refreshing ? "Refreshing…" : "Refresh"}</span>
    </button>
  );
}

function ShowArchivedToggle({ checked, onChange }) {
  return (
    <label style={styles.showAllToggle}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        style={styles.showAllCheckbox}
      />
      <span>Show archived</span>
    </label>
  );
}

function ViewModeToggle({ value, onChange }) {
  return (
    <div style={styles.viewToggle} aria-label="History view">
      <button
        type="button"
        onClick={() => onChange("list")}
        style={{
          ...styles.viewToggleButton,
          ...(value === "list" ? styles.viewToggleButtonActive : null),
        }}
        aria-pressed={value === "list"}
      >
        List
      </button>
      <button
        type="button"
        onClick={() => onChange("grid")}
        style={{
          ...styles.viewToggleButton,
          ...(value === "grid" ? styles.viewToggleButtonActive : null),
        }}
        aria-pressed={value === "grid"}
      >
        Grid
      </button>
    </div>
  );
}

function DocPreview({ item, onOpen }) {
  const current =
    item.lifecycle === "finalized"
      ? item.finalizedRevisionId || item.currentRevisionId || "D1"
      : item.currentRevisionId || item.targetRevisionId || "D1";
  const action = primaryAction(item);

  return (
    <>
      <div style={styles.previewContractTitleRow}>
        <div style={styles.previewTitleTextWrap}>
          <h3 style={styles.previewContractTitle}>{item.displayTitle}</h3>
        </div>
        <StatusPill item={item} />
      </div>

      <div style={styles.previewUpdated}>
        Last updated: {formatDateTime(item.updatedAt || item.createdAt)}
      </div>
      <div style={styles.previewDivider} />

      {item.lifecycle === "processing" ? (
        <HistoryStatusPreview
          tone="processing"
          title="Doc is processing"
          message="Your Doc is still being prepared. Open the status page to view current progress."
          actionLabel="Check Progress"
          onAction={onOpen}
        />
      ) : item.lifecycle === "error" ? (
        <HistoryStatusPreview
          tone="error"
          title="Doc did not complete"
          message="Open the status page to review what happened and the available next step."
          actionLabel="View Status"
          onAction={onOpen}
        />
      ) : (
        <>
          <h4 style={styles.previewSectionTitle}>Current state</h4>
          <p style={styles.previewSummary}>
            {item.lifecycle === "finalized"
              ? `This Doc was finalized at ${current} and opens in the Viewer.`
              : `This Doc is editable. The latest completed revision is ${current}.`}
          </p>

          <div style={styles.previewScoreGrid}>
            <PreviewInfoCard title="Latest revision" value={current} />
            <PreviewInfoCard
              title="Saved versions"
              value={String(item.revisionCount || item.revisionIds.length || 1)}
            />
          </div>

          <button type="button" onClick={onOpen} style={styles.openFullButton}>
            {action.label === "Edit" ? "Open Editor" : "Open Viewer"} ↗
          </button>
        </>
      )}
    </>
  );
}

function PreviewInfoCard({ title, value }) {
  return (
    <div style={styles.previewScoreCard}>
      <div style={styles.previewScoreTitle}>{title}</div>
      <div style={{ ...styles.previewScoreBig, color: "#2563EB", marginTop: 18 }}>
        {value}
      </div>
      <div style={styles.previewScoreLabel}>Doc Writer</div>
    </div>
  );
}

function HistoryStatusPreview({ tone, title, message, actionLabel, onAction }) {
  const isError = tone === "error";
  return (
    <div
      style={{
        ...styles.statusPreviewCard,
        ...(isError ? styles.statusPreviewCardError : null),
      }}
    >
      <div
        style={{
          ...styles.statusPreviewIcon,
          ...(isError ? styles.statusPreviewIconError : null),
        }}
      >
        {isError ? "!" : <Spinner size={22} />}
      </div>
      <h4 style={styles.statusPreviewTitle}>{title}</h4>
      <p style={styles.statusPreviewText}>{message}</p>
      <button
        type="button"
        onClick={onAction}
        style={{
          ...styles.openFullButton,
          ...(isError ? styles.openFullButtonError : null),
        }}
      >
        {actionLabel} ↗
      </button>
    </div>
  );
}

function ActionModal({
  actionModal,
  renameValue,
  setRenameValue,
  submitting,
  onCancel,
  onConfirm,
}) {
  if (!actionModal) return null;
  const item = actionModal.item || {};
  const title =
    actionModal.type === "rename"
      ? "Rename Doc"
      : actionModal.type === "delete"
        ? "Delete Doc?"
        : actionModal.type === "restore"
          ? "Restore Doc?"
          : "Archive Doc?";

  const description =
    actionModal.type === "rename"
      ? "Enter a new name for this Doc."
      : actionModal.type === "delete"
        ? "This Doc will be deleted from Doc History. This action cannot be undone."
        : actionModal.type === "restore"
          ? "This Doc will return to your main history."
          : "This Doc will move out of your main history. You can restore it using Show archived.";

  const confirmLabel =
    actionModal.type === "rename"
      ? "Save Name"
      : actionModal.type === "delete"
        ? "Delete"
        : actionModal.type === "restore"
          ? "Restore"
          : "Archive";

  const submittingLabel =
    actionModal.type === "rename"
      ? "Saving…"
      : actionModal.type === "delete"
        ? "Deleting…"
        : actionModal.type === "restore"
          ? "Restoring…"
          : "Archiving…";

  return (
    <div
      style={styles.modalOverlay}
      onMouseDown={submitting ? undefined : onCancel}
      aria-busy={submitting ? "true" : "false"}
    >
      <div style={styles.modalCard} onMouseDown={(event) => event.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <h3 style={styles.modalTitle}>{title}</h3>
            <p style={styles.modalSubtitle}>{description}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              ...styles.modalCloseButton,
              ...(submitting ? styles.modalDisabledButton : null),
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.modalItemName}>
            {cleanText(item.displayTitle || item.title, "Selected Doc")}
          </div>
          {actionModal.type === "rename" ? (
            <input
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              placeholder="Enter Doc name"
              style={styles.modalInput}
              disabled={submitting}
              autoFocus
            />
          ) : null}
        </div>

        <div style={styles.modalActions}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              ...styles.modalCancelButton,
              ...(submitting ? styles.modalDisabledButton : null),
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            style={{
              ...styles.modalConfirmButton,
              ...(actionModal.type === "delete" ? styles.modalDangerButton : null),
              ...(submitting ? styles.modalSubmittingButton : null),
            }}
          >
            {submitting ? <Spinner size={15} color="#FFFFFF" /> : null}
            <span>{submitting ? submittingLabel : confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyHistoryState() {
  return (
    <div style={styles.emptyInline}>
      <div style={styles.emptyIcon}>▤</div>
      <h2 style={styles.emptyInlineTitle}>No Docs yet</h2>
      <p style={styles.emptyInlineText}>
        Start a new Doc to create D1. Processing Docs, drafts, revisions, and
        finalized Docs will appear here automatically.
      </p>
      <Link to="/docwriter/start" style={styles.primaryButton}>
        New Doc
      </Link>
    </div>
  );
}

function Spinner({ size = 18, color = "#2563EB" }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        border: `2px solid ${color}22`,
        borderTopColor: color,
        borderRadius: "999px",
        display: "inline-block",
        animation: "docHistorySpin .8s linear infinite",
      }}
    />
  );
}

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    minHeight: "100vh",
    margin: "0 auto",
    overflowX: "hidden",
    background:
    "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
  },
  hero: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto",
    padding: "12px 0 18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
  },
  kicker: {
    color: "#2563EB",
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    fontSize: 12,
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.035em",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: 640,
  },
  newButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 18px",
    borderRadius: 12,
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#FFFFFF",
    textDecoration: "none",
    fontWeight: 900,
    boxShadow: "0 14px 28px rgba(37,99,235,0.24)",
    whiteSpace: "nowrap",
  },
  newButtonIcon: {
    fontSize: 17,
    fontWeight: 900,
  },
  layout: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 320px)",
    gap: 18,
    alignItems: "start",
  },
  layoutPreviewCollapsed: {
    gridTemplateColumns: "minmax(0, 1fr) 124px",
  },
  mainColumn: {
    minWidth: 0,
  },
  toolbarPanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 22,
    padding: 20,
    boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
  },
  controlsGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(260px, 1fr) 170px 190px 130px",
    gap: 12,
    alignItems: "center",
  },
  searchBox: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 46,
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.28)",
    borderRadius: 13,
    padding: "0 14px",
    boxShadow: "0 8px 18px rgba(15,23,42,0.03)",
  },
  searchIcon: {
    color: "#64748B",
    fontSize: 20,
  },
  searchInput: {
    minWidth: 0,
    maxWidth: "100%",
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: 14,
    color: "#0F172A",
    background: "transparent",
  },
  select: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: 46,
    borderRadius: 13,
    border: "1px solid rgba(148,163,184,0.28)",
    padding: "0 12px",
    background: "#FFFFFF",
    color: "#0F172A",
    fontWeight: 750,
    outline: "none",
  },
  filterRow: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "flex",
    gap: 10,
    marginTop: 14,
    flexWrap: "wrap",
  },
  filterButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 11,
    border: "1px solid rgba(148,163,184,0.28)",
    background: "#FFFFFF",
    color: "#334155",
    fontWeight: 850,
    cursor: "pointer",
  },
  filterButtonActive: {
    background: "#2563EB",
    color: "#FFFFFF",
    borderColor: "#2563EB",
    boxShadow: "0 10px 22px rgba(37,99,235,0.20)",
  },
  filterDot: {
    width: 8,
    height: 8,
    borderRadius: "999px",
  },
  filterSpacer: {
    flex: "1 1 auto",
    minWidth: 12,
  },
  statGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    marginTop: 16,
  },
  statCard: {
    minWidth: 0,
    overflow: "hidden",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.20)",
    borderRadius: 18,
    padding: 18,
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 12px 26px rgba(15,23,42,0.05)",
  },
  statIcon: {
    width: 52,
    height: 52,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    fontSize: 21,
  },
  statLabel: {
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  statValue: {
    color: "#0F172A",
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: "-0.03em",
    marginTop: 2,
  },
  statSubtext: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 1,
  },
  tablePanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    marginTop: 18,
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 22,
    padding: 0,
    overflow: "visible",
    isolation: "isolate",
    boxShadow: "0 20px 46px rgba(15,23,42,0.07)",
  },
  tableHeader: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(160px, 1.55fr) 104px 80px 92px 162px",
    gap: 12,
    padding: "14px 18px",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 950,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    background: "#F8FAFC",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  centerHeader: {
    textAlign: "center",
  },
  actionHeader: {
    width: "100%",
    textAlign: "center",
  },
  mobileHistoryHeader: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 116px",
    gap: 10,
    padding: "13px 18px",
    color: "#64748B",
    fontSize: 13,
    fontWeight: 950,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    background: "#F8FAFC",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  rowsWrap: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gap: 0,
  },
  rowButton: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "minmax(160px, 1.55fr) 104px 80px 92px 162px",
    gap: 12,
    alignItems: "center",
    textAlign: "left",
    border: "0",
    borderBottom: "1px solid rgba(148,163,184,0.16)",
    background: "#FFFFFF",
    padding: "15px 18px",
    cursor: "pointer",
    color: "#0F172A",
  },
  rowButtonActive: {
    background: "#F8FBFF",
    boxShadow: "inset 3px 0 0 #2563EB",
  },
  mobileRowButton: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "block",
    textAlign: "left",
    border: "0",
    borderBottom: "1px solid rgba(148,163,184,0.16)",
    background: "#FFFFFF",
    padding: "16px 18px",
    cursor: "pointer",
    color: "#0F172A",
  },
  mobileListTopRow: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "38px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 13,
  },
  mobileListTitleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  mobileListStatus: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  caseCell: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    minWidth: 0,
  },
  fileIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    fontWeight: 950,
  },
  caseTextBlock: {
    minWidth: 0,
    maxWidth: "100%",
  },
  caseTitle: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    fontWeight: 950,
    color: "#0F172A",
    whiteSpace: "nowrap",
  },
  caseTitleText: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowMetaGrid: {
    display: "grid",
    gridTemplateColumns: "110px 110px 110px 1fr",
    alignItems: "center",
    gap: 12,
  },
  centerCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    textAlign: "center",
  },
  mobileStatusCell: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    minWidth: 0,
    textAlign: "center",
  },
  mobileMetaLine: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    marginTop: 10,
    paddingLeft: 51,
  },
  mobileMetaPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 9px",
    background: "#F8FAFC",
    border: "1px solid rgba(148,163,184,0.22)",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    padding: "6px 10px",
    border: "1px solid",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 950,
    width: "fit-content",
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: "999px",
  },
  scoreBadge: {
    display: "inline-flex",
    justifyContent: "center",
    width: "fit-content",
    color: "#047857",
    background: "#ECFDF5",
    border: "1px solid #BBF7D0",
    padding: "6px 10px",
    borderRadius: 9,
    fontSize: 12,
    fontWeight: 950,
  },
  pageCount: {
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  updatedText: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 750,
    lineHeight: 1.35,
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
  },
  rowActions: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "112px 42px",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  mobileRowActions: {
    width: "100%",
    maxWidth: "100%",
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    minWidth: 0,
    paddingTop: 8,
  },
  mobileListActions: {
    gridColumn: "1 / -1",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    paddingTop: 8,
  },
  openButton: {
    width: 112,
    minWidth: 112,
    maxWidth: 112,
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    textAlign: "center",
    border: "1px solid rgba(37,99,235,0.38)",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: 10,
    padding: "8px 12px",
    fontWeight: 950,
    cursor: "pointer",
  },
  progressButton: {
    background: "#EFF6FF",
    borderColor: "#93C5FD",
    color: "#1D4ED8",
  },
  errorStatusButton: {
    background: "#FEF2F2",
    borderColor: "#FCA5A5",
    color: "#B91C1C",
  },
  pendingScoreBadge: {
    color: "#64748B",
    background: "#F8FAFC",
    borderColor: "#E2E8F0",
  },
  pdfButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    border: "1px solid rgba(148,163,184,0.30)",
    background: "#FFFFFF",
    color: "#0F172A",
    borderRadius: 10,
    padding: "8px 12px",
    fontWeight: 900,
    textDecoration: "none",
    fontSize: 13,
    minWidth: 44,
    textAlign: "center",
  },

  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 11,
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    color: "#1D4ED8",
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },
  refreshButtonDisabled: {
    cursor: "wait",
    opacity: 0.72,
  },
  refreshIcon: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 900,
  },
  showAllToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 11,
    border: "1px solid rgba(148,163,184,0.28)",
    background: "#FFFFFF",
    color: "#334155",
    fontWeight: 850,
    cursor: "pointer",
  },
  showAllCheckbox: {
    width: 15,
    height: 15,
    accentColor: "#2563EB",
  },
  archivedInline: {
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "2px 7px",
    background: "#F1F5F9",
    color: "#475569",
    fontSize: 10,
    fontWeight: 950,
    verticalAlign: "middle",
  },
  menuWrapper: {
    position: "relative",
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
  },
  moreButton: {
    width: 42,
    height: 42,
    flex: "0 0 42px",
    border: "1px solid rgba(148,163,184,0.28)",
    borderRadius: 11,
    background: "#FFFFFF",
    color: "#475569",
    fontWeight: 950,
    fontSize: 21,
    padding: 0,
    cursor: "pointer",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionMenu: {
    position: "fixed",
    width: 176,
    maxWidth: "calc(100vw - 20px)",
    background: "#FFFFFF",
    color: "#0F172A",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 12,
    boxShadow: "0 18px 38px rgba(15,23,42,0.18)",
    zIndex: 1200,
    overflow: "hidden",
  },
  actionMenuItem: {
    display: "block",
    width: "100%",
    border: "none",
    background: "#FFFFFF",
    color: "#0F172A",
    textAlign: "left",
    padding: "10px 12px",
    fontWeight: 850,
    cursor: "pointer",
  },
  actionMenuItemDanger: {
    color: "#DC2626",
  },

  moreDots: {
    color: "#64748B",
    fontWeight: 950,
    fontSize: 18,
    padding: "0 2px",
  },
  processingMini: {
    width: 140,
    color: "#475569",
    fontSize: 12,
    fontWeight: 800,
  },
  processingTrack: {
    marginTop: 6,
    height: 7,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  processingFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #2563EB, #1D4ED8)",
  },
  pagination: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    background: "#FFFFFF",
  },
  paginationText: {
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  pageButtons: {
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  pageButton: {
    width: 34,
    height: 34,
    borderRadius: 9,
    border: "1px solid rgba(148,163,184,0.30)",
    background: "#FFFFFF",
    color: "#334155",
    fontWeight: 950,
    cursor: "pointer",
  },
  pageButtonActive: {
    background: "#EFF6FF",
    borderColor: "#2563EB",
    color: "#2563EB",
  },
  currentPageButton: {
    cursor: "default",
    opacity: 1,
  },
  pageButtonLoading: {
    cursor: "wait",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.10)",
  },
  paginationLoading: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
  pageEllipsis: {
    color: "#64748B",
    padding: "0 4px",
    fontWeight: 950,
  },
  mobileUtilityRow: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    marginTop: 14,
    display: "none",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  resultsToolbar: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    padding: "14px 16px",
    display: "none",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    background: "#F8FAFC",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  resultsEyebrow: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
  },
  resultsCount: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
    marginTop: 2,
  },
  viewToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: 4,
    borderRadius: 11,
    background: "#E2E8F0",
    flex: "0 0 auto",
  },
  viewToggleButton: {
    minHeight: 34,
    padding: "0 11px",
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "#475569",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  viewToggleButtonActive: {
    background: "#FFFFFF",
    color: "#1D4ED8",
    boxShadow: "0 4px 12px rgba(15,23,42,0.10)",
  },
  mobileCardGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: 12,
    padding: 12,
    background: "#F8FAFC",
  },
  mobileCard: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
  },
  mobileCardActive: {
    borderColor: "#93C5FD",
    boxShadow: "0 0 0 2px #DBEAFE, 0 12px 26px rgba(37,99,235,0.10)",
  },
  mobileCardHeader: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 11,
  },
  mobileCardStatusGroup: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    flexWrap: "wrap",
  },
  mobileCardIdentity: {
    display: "flex",
    alignItems: "flex-start",
    gap: 11,
    minWidth: 0,
    flex: "1 1 auto",
  },
  mobileCardIcon: {
    width: 42,
    height: 42,
    flex: "0 0 42px",
    borderRadius: 13,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
  },
  mobileCardTitleWrap: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  mobileCardTitle: {
    minWidth: 0,
    maxWidth: "100%",
    margin: 0,
    color: "#0F172A",
    fontSize: 15,
    lineHeight: 1.35,
    fontWeight: 950,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflowWrap: "normal",
    wordBreak: "normal",
  },
  mobileCardStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14,
  },
  archivedCardBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    borderRadius: 999,
    padding: "0 10px",
    background: "#F1F5F9",
    color: "#475569",
    fontSize: 11,
    fontWeight: 900,
  },
  mobileCardMetrics: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 14,
    marginBottom: 14,
  },
  mobileMetric: {
    minWidth: 0,
    borderRadius: 13,
    background: "#F8FAFC",
    border: "1px solid rgba(148,163,184,0.18)",
    padding: "11px 12px",
  },
  mobileMetricLabel: {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 800,
  },
  mobileMetricValue: {
    display: "block",
    color: "#0F172A",
    fontSize: 17,
    fontWeight: 950,
    marginTop: 3,
  },
  mobileMetricDate: {
    display: "block",
    color: "#0F172A",
    fontSize: 12,
    lineHeight: 1.35,
    fontWeight: 900,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  mobileHistoryActions: {
    gridColumn: "1 / -1",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    paddingTop: 8,
  },
  previewPanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    position: "sticky",
    top: 20,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 22,
    padding: 22,
    boxShadow: "0 24px 52px rgba(15,23,42,0.10)",
  },
  previewCollapsedPanel: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    position: "sticky",
    top: 20,
    display: "flex",
    justifyContent: "center",
  },
  showPreviewButton: {
    width: "100%",
    minHeight: 48,
    border: "1px solid rgba(37,99,235,0.30)",
    borderRadius: 16,
    background: "#FFFFFF",
    color: "#2563EB",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(15,23,42,0.08)",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(148,163,184,0.20)",
    paddingBottom: 14,
    marginBottom: 16,
  },
  previewTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  previewChevron: {
    color: "#0F172A",
    fontWeight: 950,
  },
  collapsePreviewButton: {
    border: "1px solid rgba(148,163,184,0.26)",
    background: "#FFFFFF",
    color: "#334155",
    borderRadius: 10,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  previewContractTitleRow: {
    minWidth: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  previewTitleTextWrap: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  previewContractTitle: {
    maxWidth: "100%",
    overflowWrap: "anywhere",
    margin: 0,
    fontSize: 16,
    color: "#0F172A",
    fontWeight: 950,
    lineHeight: 1.25,
  },
  previewUpdated: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 12,
  },
  previewDivider: {
    height: 1,
    background: "rgba(148,163,184,0.20)",
    margin: "16px 0",
  },
  previewSectionTitle: {
    margin: "14px 0 8px",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 950,
  },
  previewSummary: {
    maxWidth: "100%",
    overflowWrap: "anywhere",
    margin: 0,
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.65,
    fontWeight: 650,
  },
  previewScoreGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 16,
  },
  previewScoreCard: {
    minWidth: 0,
    minHeight: 146,
    overflow: "hidden",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 16,
    padding: 12,
    background: "#FFFFFF",
    textAlign: "center",
    boxShadow: "0 10px 24px rgba(15,23,42,0.045)",
  },
  previewScoreTitle: {
    minHeight: 30,
    color: "#0F172A",
    fontSize: 11.5,
    fontWeight: 950,
    lineHeight: 1.25,
    display: "grid",
    placeItems: "center",
    overflowWrap: "anywhere",
  },
  previewGaugeWrap: {
    minHeight: 72,
    display: "grid",
    placeItems: "center",
  },
  previewGauge: {
    width: 68,
    height: 68,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
  },
  previewGaugeInner: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#FFFFFF",
    display: "grid",
    placeItems: "center",
    alignContent: "center",
    boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
  },
  previewGaugeValue: {
    fontWeight: 950,
    fontSize: 18,
    lineHeight: 1,
  },
  previewScoreBarWrap: {
    minHeight: 72,
    display: "grid",
    alignContent: "center",
    gap: 9,
  },
  previewScoreBarTop: {
    display: "flex",
    justifyContent: "center",
    alignItems: "baseline",
    gap: 3,
  },
  previewScoreBig: {
    fontWeight: 950,
    fontSize: 26,
    lineHeight: 1,
  },
  previewScoreUnit: {
    color: "#64748B",
    fontWeight: 800,
    fontSize: 9,
  },
  previewScoreTrack: {
    height: 8,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  previewScoreFill: {
    height: "100%",
    borderRadius: 999,
  },
  previewScoreLabel: {
    color: "#64748B",
    fontWeight: 750,
    fontSize: 10.5,
    lineHeight: 1.25,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  previewLoadingState: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    color: "#475569",
    fontWeight: 850,
  },
  statusPreviewCard: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    textAlign: "center",
    border: "1px solid #BFDBFE",
    borderRadius: 18,
    background: "#EFF6FF",
    padding: 22,
    overflow: "hidden",
  },
  statusPreviewCardError: {
    borderColor: "#FECACA",
    background: "#FEF2F2",
  },
  statusPreviewIcon: {
    width: 48,
    height: 48,
    margin: "0 auto 12px",
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "#DBEAFE",
    color: "#1D4ED8",
    fontSize: 22,
    fontWeight: 950,
  },
  statusPreviewIconError: {
    background: "#FEE2E2",
    color: "#B91C1C",
  },
  statusPreviewTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },
  statusPreviewText: {
    margin: "10px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  openFullButtonError: {
    background: "linear-gradient(135deg, #DC2626, #B91C1C)",
  },
  openFullButton: {
    width: "100%",
    marginTop: 20,
    border: "none",
    borderRadius: 13,
    padding: "13px 14px",
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#FFFFFF",
    fontWeight: 950,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(37,99,235,0.24)",
  },
  previewEmpty: {
    textAlign: "center",
    padding: "28px 10px",
    color: "#475569",
  },
  emptyState: {
    maxWidth: 720,
    margin: "80px auto",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 24,
    padding: 34,
    textAlign: "center",
    boxShadow: "0 22px 48px rgba(15,23,42,0.08)",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    margin: "0 auto 14px",
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "#EFF6FF",
    color: "#2563EB",
    fontSize: 26,
    fontWeight: 950,
  },
  emptyTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 28,
    fontWeight: 950,
  },
  emptyText: {
    margin: "10px auto 20px",
    color: "#475569",
    maxWidth: 540,
    lineHeight: 1.6,
  },
  emptyInline: {
    padding: "54px 20px",
    textAlign: "center",
  },
  emptyInlineTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
  },
  emptyInlineText: {
    margin: "8px auto 18px",
    color: "#64748B",
    maxWidth: 520,
    lineHeight: 1.6,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    padding: "12px 16px",
    background: "#2563EB",
    color: "#FFFFFF",
    textDecoration: "none",
    fontWeight: 950,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 5000,
    background: "rgba(15,23,42,0.38)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    width: "min(520px, 100%)",
    background: "#FFFFFF",
    borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.24)",
    boxShadow: "0 30px 80px rgba(15,23,42,0.28)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    padding: "22px 24px 16px",
    borderBottom: "1px solid rgba(148,163,184,0.18)",
  },
  modalTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 20,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  modalSubtitle: {
    margin: "7px 0 0",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 650,
  },
  modalCloseButton: {
    border: "none",
    background: "#F1F5F9",
    color: "#334155",
    width: 34,
    height: 34,
    borderRadius: 999,
    fontSize: 22,
    lineHeight: "30px",
    cursor: "pointer",
    fontWeight: 800,
  },
  modalBody: {
    padding: "18px 24px 8px",
  },
  modalItemName: {
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 900,
    marginBottom: 12,
  },
  modalInput: {
    width: "100%",
    minHeight: 46,
    border: "1px solid rgba(148,163,184,0.34)",
    borderRadius: 12,
    padding: "0 14px",
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "18px 24px 22px",
  },
  modalCancelButton: {
    border: "1px solid rgba(148,163,184,0.34)",
    background: "#FFFFFF",
    color: "#334155",
    borderRadius: 12,
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  modalConfirmButton: {
    border: "none",
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#FFFFFF",
    borderRadius: 12,
    padding: "10px 16px",
    minWidth: 112,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(37,99,235,0.20)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  modalDangerButton: {
    background: "linear-gradient(135deg, #DC2626, #B91C1C)",
    boxShadow: "0 12px 24px rgba(220,38,38,0.20)",
  },
  modalSubmittingButton: {
    cursor: "wait",
    opacity: 0.88,
    boxShadow: "none",
  },
  modalDisabledButton: {
    cursor: "not-allowed",
    opacity: 0.58,
  },

  errorBox: {
    maxWidth: "100%",
    margin: "0 auto 16px",
    background: "#FEF2F2",
    color: "#991B1B",
    border: "1px solid #FECACA",
    borderRadius: 14,
    padding: "12px 14px",
    fontWeight: 750,
  },
  loadingCard: {
    maxWidth: 520,
    margin: "90px auto",
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 22,
    padding: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
  },
  loadingTitle: {
    fontWeight: 900,
    color: "#0F172A",
  },
  loadingRows: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    color: "#475569",
    fontWeight: 850,
  },


};
